/**
 * Smart Body Map™ — interactive front+back body chart for fisio/osteo tenants.
 *
 * Centerpiece of the Wellness Suite. Mobile-first, color-coded, premium
 * medical-chart aesthetic. Click any anatomical region → side panel opens
 * with status / pain / stiffness / inflammation / mobility / trigger /
 * clinical notes. Every change is timestamped and audit-logged.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Activity, Loader2, X, Sparkles, RotateCw, Flame, Snowflake,
  CheckCircle2, AlertCircle, Eraser, ShieldCheck, Layers,
} from "lucide-react";

/* ============================================================ */
/* SVG ZONE GEOMETRY                                            */
/* ============================================================ */
/* Each zone is a CSS-stylable path. Coordinates designed on a   */
/* 360x720 viewBox (figure stylized, ratio matches real human).  */
const FRONT_ZONES = {
  head:       "M180,30 m-32,0 a32,40 0 1 0 64,0 a32,40 0 1 0 -64,0",
  cervical:   "M168,76 h24 v22 h-24 z",
  shoulder_l: "M138,100 q-26,2 -32,28 l34,4 z",
  shoulder_r: "M222,100 q26,2 32,28 l-34,4 z",
  chest:      "M148,108 h64 v74 h-64 z",
  elbow_l:    "M96,210 q-14,6 -12,22 l24,-6 z",
  elbow_r:    "M264,210 q14,6 12,22 l-24,-6 z",
  abdomen:    "M152,184 h56 v62 h-56 z",
  wrist_l:    "M70,308 q-10,8 -2,22 l20,-8 z",
  wrist_r:    "M290,308 q10,8 2,22 l-20,-8 z",
  hip_l:      "M148,248 q-14,16 -8,40 l32,-4 v-32 z",
  hip_r:      "M212,248 q14,16 8,40 l-32,-4 v-32 z",
  knee_l:     "M138,420 q-6,10 0,24 h28 v-22 z",
  knee_r:     "M194,420 q6,10 0,24 h-28 v-22 z",
  ankle_l:    "M138,600 q-4,10 4,22 h22 v-22 z",
  ankle_r:    "M194,600 q4,10 -4,22 h-22 v-22 z",
};

const BACK_ZONES = {
  trapezius:  "M138,98 q42,-6 84,0 l-4,28 h-76 z",
  dorsal:     "M148,128 h64 v60 h-64 z",
  lumbar:     "M156,188 h48 v44 h-48 z",
  sacrum:     "M168,232 h24 v22 h-24 z",
  glute_l:    "M146,254 q-10,20 -2,44 l32,-4 v-40 z",
  glute_r:    "M214,254 q10,20 2,44 l-32,-4 v-40 z",
  hamstring_l:"M138,304 q-6,40 0,80 l30,-2 v-78 z",
  hamstring_r:"M194,304 q6,40 0,80 l-30,-2 v-78 z",
};

/* Stylized human silhouette (front + back) — same outline mirror */
const SILHOUETTE = "M180,12 c20,0 32,16 32,38 c0,18 -10,32 -22,38 c14,4 30,16 32,36 c2,18 -10,40 -10,68 l8,86 c1,16 6,30 14,42 c8,12 14,28 14,52 l-6,80 c-2,18 -6,40 -10,60 c-4,22 -10,42 -10,68 l4,90 c0,12 -6,22 -16,22 c-10,0 -16,-10 -16,-22 l-2,-90 c-2,-2 -6,-2 -8,0 l-2,90 c0,12 -6,22 -16,22 c-10,0 -16,-10 -16,-22 l4,-90 c0,-26 -6,-46 -10,-68 c-4,-20 -8,-42 -10,-60 l-6,-80 c0,-24 6,-40 14,-52 c8,-12 13,-26 14,-42 l8,-86 c0,-28 -12,-50 -10,-68 c2,-20 18,-32 32,-36 c-12,-6 -22,-20 -22,-38 c0,-22 12,-38 32,-38 z";

const SmartBodyMap = ({ clientId, clientName }) => {
  const [zones, setZones]       = useState([]);
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading]   = useState(true);
  const [view, setView]         = useState("front");  // front | back
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/wellness/body-map/${clientId}`, { withCredentials: true });
      setZones(r.data.zones || []);
      setStatuses(r.data.statuses || {});
    } catch {
      toast.error("Errore caricamento mappa corporea");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const byCode = useMemo(
    () => Object.fromEntries(zones.map(z => [z.code, z])),
    [zones]
  );

  const visibleGeometry = view === "front" ? FRONT_ZONES : BACK_ZONES;
  const visibleZones    = zones.filter(z => z.view === view);

  const issueCount = zones.filter(z => z.status !== "ok" && z.status !== "resolved").length;

  if (loading && zones.length === 0) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4">
        <div className="rounded-3xl bg-slate-100 dark:bg-white/5 h-[420px] animate-pulse" />
        <div className="rounded-3xl bg-slate-100 dark:bg-white/5 h-[420px] animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="smart-body-map">
      {/* HEADER */}
      <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-gradient-to-br from-cyan-500/5 via-transparent to-emerald-500/5 dark:from-cyan-500/10 dark:to-emerald-500/10 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-600 dark:text-cyan-400">Smart Body Map</p>
          <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">Mappa corporea clinica</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {issueCount === 0
              ? "Nessuna criticità attiva"
              : <>{issueCount} {issueCount === 1 ? "area" : "aree"} da monitorare</>}
          </p>
        </div>
        <div className="inline-flex p-0.5 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10">
          {[
            { key: "front", label: "Anteriore" },
            { key: "back",  label: "Posteriore" },
          ].map(v => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              data-testid={`body-map-view-${v.key}`}
              className={`px-4 py-1.5 text-xs font-bold rounded-full transition-all ${
                view === v.key
                  ? "bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow"
                  : "text-slate-500"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-4">
        {/* BODY SVG */}
        <div className="rounded-3xl bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-950 border border-slate-200/60 dark:border-white/10 p-4 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-30 dark:opacity-50"
               style={{ background: "radial-gradient(circle at 50% 30%, rgba(14,165,233,0.18), transparent 60%)" }} />
          <svg viewBox="0 0 360 720" className="w-full h-auto max-h-[520px] mx-auto block relative" data-testid={`body-svg-${view}`}>
            {/* Silhouette */}
            <path d={SILHOUETTE} fill="rgb(241 245 249)" stroke="rgb(148 163 184)" strokeWidth="1.5" opacity="0.55" />

            {/* Zones */}
            {visibleZones.map(z => {
              const meta = statuses[z.status] || statuses.ok || { color: "#cbd5e1" };
              const isSelected = selected?.code === z.code;
              return (
                <g
                  key={z.code}
                  onClick={() => setSelected(byCode[z.code])}
                  style={{ cursor: "pointer" }}
                  data-testid={`body-zone-${z.code}`}
                >
                  <path
                    d={visibleGeometry[z.code]}
                    fill={meta.color}
                    fillOpacity={z.status === "ok" ? 0.18 : 0.78}
                    stroke={isSelected ? "#0f172a" : meta.color}
                    strokeWidth={isSelected ? 2.5 : 1}
                    style={{ transition: "all 0.2s" }}
                  />
                  {z.trigger_point && (
                    <circle
                      cx={getZoneCenter(visibleGeometry[z.code]).cx}
                      cy={getZoneCenter(visibleGeometry[z.code]).cy}
                      r="5"
                      fill="#0ea5e9" stroke="#fff" strokeWidth="1.5"
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="flex flex-wrap gap-1.5 justify-center mt-3">
            {Object.entries(statuses).filter(([k]) => k !== "ok").slice(0, 6).map(([k, m]) => (
              <span key={k} className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10">
                <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                {m.label}
              </span>
            ))}
          </div>
        </div>

        {/* SIDE PANEL */}
        <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900 p-4">
          {selected ? (
            <ZoneEditor
              zone={selected}
              statuses={statuses}
              onClose={() => setSelected(null)}
              onSaved={() => { setSelected(null); load(); }}
              clientId={clientId}
            />
          ) : (
            <EmptyHint zones={visibleZones} onPick={(z) => setSelected(z)} statuses={statuses} />
          )}
        </div>
      </div>
    </div>
  );
};

/* Helper: extract approximate center from an SVG path d-string for badge placement */
function getZoneCenter(d) {
  if (!d) return { cx: 0, cy: 0 };
  // crude parse: take first M and following h/v offsets to estimate centroid
  const nums = (d.match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
  if (nums.length < 4) return { cx: 0, cy: 0 };
  // Average of x and y candidates (every even index = x, odd = y in M h v contexts)
  const xs = nums.filter((_, i) => i % 2 === 0).slice(0, 6);
  const ys = nums.filter((_, i) => i % 2 === 1).slice(0, 6);
  const avg = arr => arr.reduce((a, b) => a + b, 0) / arr.length;
  return { cx: avg(xs), cy: avg(ys) };
}

/* ============================================================ */
/* EMPTY HINT                                                    */
/* ============================================================ */
const EmptyHint = ({ zones, onPick, statuses }) => {
  const issues = zones.filter(z => z.status !== "ok" && z.status !== "resolved");
  return (
    <div className="space-y-3">
      <div className="text-center py-3">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-100 to-emerald-100 dark:from-cyan-900/30 dark:to-emerald-900/30 flex items-center justify-center mb-2">
          <Activity className="w-5 h-5 text-cyan-600 dark:text-cyan-300" />
        </div>
        <h4 className="font-bold text-slate-900 dark:text-white">Seleziona una zona</h4>
        <p className="text-xs text-slate-500 mt-1">Tocca un'area del corpo per registrare lo stato clinico.</p>
      </div>

      {issues.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Zone attive ({issues.length})</p>
          <ul className="space-y-1">
            {issues.map(z => {
              const meta = statuses[z.status] || {};
              return (
                <li key={z.code}>
                  <button
                    onClick={() => onPick(z)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-white/5 hover:bg-slate-100 dark:hover:bg-white/10 text-left transition-all"
                    data-testid={`body-issue-${z.code}`}
                  >
                    <span className="inline-flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{z.label}</span>
                    </span>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{meta.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ============================================================ */
/* ZONE EDITOR                                                  */
/* ============================================================ */
const ZoneEditor = ({ zone, statuses, onClose, onSaved, clientId }) => {
  const [status,       setStatus]       = useState(zone.status || "ok");
  const [pain,         setPain]         = useState(zone.pain_level ?? 0);
  const [stiffness,    setStiffness]    = useState(zone.stiffness ?? 0);
  const [inflammation, setInflammation] = useState(zone.inflammation ?? 0);
  const [mobility,     setMobility]     = useState(zone.mobility ?? 10);
  const [trigger,      setTrigger]      = useState(!!zone.trigger_point);
  const [notes,        setNotes]        = useState(zone.notes || "");
  const [saving,       setSaving]       = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/wellness/body-map/${clientId}/zones/${zone.code}`, {
        status, pain_level: pain, stiffness, inflammation, mobility,
        trigger_point: trigger, notes: notes.trim() || null,
      }, { withCredentials: true });
      toast.success("Zona aggiornata");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetZone = async () => {
    if (!window.confirm("Reset stato della zona? La modifica verrà tracciata nella timeline.")) return;
    try {
      await axios.delete(`${API}/wellness/body-map/${clientId}/zones/${zone.code}`, { withCredentials: true });
      toast.success("Zona resettata");
      onSaved();
    } catch {
      toast.error("Errore reset");
    }
  };

  return (
    <div className="space-y-3" data-testid={`zone-editor-${zone.code}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-600 dark:text-cyan-400">
            {zone.view === "front" ? "Vista anteriore" : "Vista posteriore"}
          </p>
          <h4 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">{zone.label}</h4>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1" data-testid="zone-editor-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* STATUS pills */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Stato clinico</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(statuses).map(([key, m]) => (
            <button
              key={key}
              onClick={() => setStatus(key)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold border-2 transition-all ${
                status === key
                  ? "border-slate-900 dark:border-white"
                  : "border-transparent hover:border-slate-200"
              }`}
              style={{
                background: status === key ? m.color : "transparent",
                color: status === key ? "#fff" : m.color,
              }}
              data-testid={`zone-status-${key}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: status === key ? "#fff" : m.color }} />
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sliders */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Slider icon={Flame}      color="text-rose-500"    label="Dolore"        value={pain}         set={setPain} />
        <Slider icon={Layers}     color="text-violet-500"  label="Rigidità"      value={stiffness}    set={setStiffness} />
        <Slider icon={Flame}      color="text-orange-500"  label="Infiammazione" value={inflammation} set={setInflammation} />
        <Slider icon={RotateCw}   color="text-emerald-500" label="Mobilità"      value={mobility}     set={setMobility} hint="0=bloccata · 10=piena" />
      </div>

      {/* Trigger point toggle */}
      <label
        className={`flex items-center justify-between gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all ${
          trigger ? "border-cyan-500 bg-cyan-50/60 dark:bg-cyan-900/20" : "border-slate-200 dark:border-white/10"
        }`}
        data-testid="zone-trigger-toggle"
      >
        <span className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 dark:text-slate-200">
          <Sparkles className={`w-3.5 h-3.5 ${trigger ? "text-cyan-500" : "text-slate-400"}`} />
          Trigger point presente
        </span>
        <input type="checkbox" checked={trigger} onChange={e => setTrigger(e.target.checked)} className="sr-only" />
        <span className={`w-9 h-5 rounded-full p-0.5 transition-all ${trigger ? "bg-cyan-500" : "bg-slate-300"}`}>
          <span className={`block w-4 h-4 rounded-full bg-white transition-all ${trigger ? "translate-x-4" : ""}`} />
        </span>
      </label>

      {/* Notes */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Note cliniche</p>
        <Textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Es: rigidità mattutina, irradiazione alla gamba dx…"
          className="text-sm min-h-[64px]"
          data-testid="zone-notes"
        />
      </div>

      {zone.updated_at && (
        <p className="text-[10px] text-slate-400">
          Ultimo aggiornamento: {new Date(zone.updated_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
          {zone.updated_by && <> · da <strong className="text-slate-500">{zone.updated_by}</strong></>}
        </p>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          size="sm" variant="outline"
          onClick={resetZone}
          className="rounded-full text-xs"
          data-testid="zone-reset-btn"
        >
          <Eraser className="w-3.5 h-3.5 mr-1" /> Reset
        </Button>
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          className="flex-1 rounded-full bg-gradient-to-r from-cyan-600 to-emerald-500 hover:opacity-90 text-white"
          data-testid="zone-save-btn"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
          {saving ? "Salvataggio…" : "Salva valutazione"}
        </Button>
      </div>
    </div>
  );
};

const Slider = ({ icon: Icon, color, label, value, set, hint }) => (
  <div className="rounded-2xl bg-slate-50 dark:bg-white/5 border border-slate-200/60 dark:border-white/10 p-2.5">
    <div className="flex items-center justify-between mb-1">
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        <Icon className={`w-3 h-3 ${color}`} /> {label}
      </span>
      <span className="text-xs font-black text-slate-900 dark:text-white font-variant-numeric tabular-nums">{value}<span className="text-slate-400 font-normal text-[10px]">/10</span></span>
    </div>
    <input
      type="range" min="0" max="10" value={value}
      onChange={e => set(Number(e.target.value))}
      className="w-full accent-cyan-500"
      data-testid={`slider-${label.toLowerCase()}`}
    />
    {hint && <p className="text-[9px] text-slate-400 mt-0.5">{hint}</p>}
  </div>
);

export default SmartBodyMap;
